import { config } from "@repo/config";
import { CreateOrganizationForm } from "@saas/organizations/components/CreateOrganizationForm";
import { AuthWrapper } from "@saas/shared/components/AuthWrapper";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

/** Open invitations for the signed-in email: someone who signed in without the link still finds them. */
const getPendingInvitationsFn = createServerFn({ method: "GET" }).handler(
	async () => {
		// Dynamic imports to prevent server code from being bundled for client
		const [{ authApi }, { db }] = await Promise.all([
			import("@repo/auth"),
			import("@repo/database"),
		]);
		const invitations = await authApi
			.listUserInvitations({ headers: getRequest().headers })
			.catch(() => []);
		const open = invitations.filter(
			(invitation) =>
				new Date(invitation.expiresAt).getTime() > Date.now(),
		);
		if (open.length === 0) {
			return [];
		}
		const organizations = await db.organization.findMany({
			where: {
				id: { in: open.map((invitation) => invitation.organizationId) },
			},
			select: { id: true, slug: true, logo: true },
		});
		return open.flatMap((invitation) => {
			const organization = organizations.find(
				(org) => org.id === invitation.organizationId,
			);
			return organization?.slug
				? [
						{
							id: invitation.id,
							role: invitation.role,
							organizationName: invitation.organizationName,
							organizationSlug: organization.slug,
							logoUrl: organization.logo,
						},
					]
				: [];
		});
	},
);

// Where a signed-in person with no retailer lands. Retailers are created by
// platform staff, so this offers their open invitations or explains how to
// get one.
export const Route = createFileRoute("/_saas/new-organization")({
	beforeLoad: ({ context }) => {
		const { organizations } = context;

		if (
			!config.organizations.enable ||
			(!config.organizations.enableUsersToCreateOrganizations &&
				(!config.organizations.requireOrganization ||
					organizations.length > 0))
		) {
			throw redirect({ to: "/app" });
		}
	},
	loader: () => getPendingInvitationsFn(),
	head: () => ({
		meta: [{ title: `No team yet - ${config.appName}` }],
	}),
	component: NewOrganizationPage,
});

function NewOrganizationPage() {
	const { session } = Route.useRouteContext();
	const invitations = Route.useLoaderData();
	return (
		<AuthWrapper>
			<CreateOrganizationForm
				signedInAs={session.user.email}
				invitations={invitations}
			/>
		</AuthWrapper>
	);
}
